import { notify } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { FileListItem } from '@views/documents/elements/FileList'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { deleteDocument, deleteFolder } from '../query-api'

interface UseDeleteItemsProps {
  items: FileListItem[]
  onSuccess?: () => void
}

export const useDeleteItems = (props: UseDeleteItemsProps) => {
  const { items, onSuccess } = props
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries(QueryKeys.DmsFolders)
    queryClient.invalidateQueries(QueryKeys.DmsFolderContents)
  }, [queryClient])

  const { mutate: deleteSingleItem, isLoading: isDeletingSingle } = useMutation(
    async (id: string) => {
      if (!organization) {
        return
      }
      const item = items.find((i) => i.id === id)
      if (!item) {
        return
      }

      if (item.type === 'folder') {
        await deleteFolder({ organizationId: organization.id, folderId: id })
      } else {
        await deleteDocument({ organizationId: organization.id, documentId: id })
      }
    },
    {
      onSuccess: (_, id) => {
        invalidateQueries()
        const item = items.find((i) => i.id === id)
        notify({
          id: NotificationKeys.DocumentsDeleteDocument,
          message: t('documents.delete_document.notification.success', { name: item?.name ?? '' }),
          variant: 'success',
        })
        onSuccess?.()
      },
      onError: () => {
        notify({
          id: NotificationKeys.DocumentsDeleteDocument,
          message: t('documents.delete_document.notification.error'),
          variant: 'error',
        })
      },
    },
  )

  const { mutate: deleteBulkItems, isLoading: isDeletingBulk } = useMutation(
    async (ids: string[]) => {
      if (!organization) {
        return
      }

      await Promise.all(
        ids.map((id) => {
          const item = items.find((i) => i.id === id)
          if (!item) {
            return Promise.resolve()
          }

          if (item.type === 'folder') {
            return deleteFolder({ organizationId: organization.id, folderId: id })
          }
          return deleteDocument({ organizationId: organization.id, documentId: id })
        }),
      )
    },
    {
      onSuccess: (_, ids) => {
        invalidateQueries()
        notify({
          id: NotificationKeys.DocumentsDeleteDocument,
          message: t('documents.bulk_delete.notification.success', { count: ids.length }),
          variant: 'success',
        })
        onSuccess?.()
      },
      onError: () => {
        invalidateQueries()
        notify({
          id: NotificationKeys.DocumentsDeleteDocument,
          message: t('documents.bulk_delete.notification.error'),
          variant: 'error',
        })
      },
    },
  )

  return useMemo(
    () => ({
      deleteItem: deleteSingleItem,
      deleteBulkItems,
      isProcessing: isDeletingSingle || isDeletingBulk,
    }),
    [deleteSingleItem, deleteBulkItems, isDeletingSingle, isDeletingBulk],
  )
}
